import { z } from 'zod';

/**
 * Outbound webhooks (BB-142, CH-005): an environment registers its endpoints (URL, method, headers, secrets, typed
 * params); a message on the `webhook` channel picks one endpoint and fills its params with Liquid. Only registered
 * endpoints on allowlisted domains are called — never an arbitrary URL from a message. Secrets are references
 * (`env:NAME` | `file:/abs/path` under `BB_SECRETS_DIR`, as push credentials); their values are never stored or returned.
 */
export const WEBHOOK_METHODS = ['POST', 'PUT', 'PATCH'] as const;
export type WebhookMethod = (typeof WEBHOOK_METHODS)[number];

export const WEBHOOK_PARAM_TYPES = ['string', 'number', 'integer', 'boolean', 'json'] as const;
export type WebhookParamType = (typeof WEBHOOK_PARAM_TYPES)[number];

export const WEBHOOK_CONTENT_TYPES = ['application/json', 'text/plain', 'application/x-www-form-urlencoded'] as const;
export type WebhookContentType = (typeof WEBHOOK_CONTENT_TYPES)[number];

/** Per-request timeout (Braze: 120 s; ours at most 10 s) and attempts including the first (Braze: up to 5). */
export const WEBHOOK_TIMEOUT_MS = { min: 1_000, max: 10_000, default: 10_000 } as const;
export const WEBHOOK_MAX_ATTEMPTS = { min: 1, max: 5, default: 5 } as const;
/** Largest rendered body, and largest template of a body. */
export const WEBHOOK_BODY_MAX_BYTES = 64 * 1024;

/** Headers the sender sets itself; an endpoint may not set them. */
export const WEBHOOK_RESERVED_HEADERS = ['host', 'content-length', 'content-type', 'transfer-encoding', 'connection', 'user-agent', 'x-bb-signature', 'x-bb-timestamp', 'x-bb-idempotency-key', 'x-bb-delivery-id', 'x-bb-attempt'] as const;

/** Header names that carry credentials: they belong in `secretHeaders`, never in plain `headers`. */
export const looksLikeSecretHeader = (name: string): boolean => /authorization|cookie|token|secret|password|passwd|api[-_]?key|signature|session|credential/i.test(name);

/**
 * A URL template: a literal origin (`https://host[:port]`, no user info) and an optional path/query that may use
 * Liquid (`/users/{{ params.user_id }}`). Returns the origin, or why the template cannot be one.
 */
export function splitWebhookUrl(template: string): { ok: true; origin: string; protocol: 'https:' | 'http:'; hostname: string; port: string; rest: string } | { ok: false; message: string } {
  const m = /^(https?):\/\/([^/?#]*)(.*)$/i.exec(template.trim());
  if (!m) return { ok: false, message: 'the URL must start with https://' };
  const authority = m[2]!;
  if (!authority) return { ok: false, message: 'the URL needs a host' };
  if (/[{}%\s]/.test(authority)) return { ok: false, message: 'the host must be literal (no Liquid in scheme, host or port)' };
  if (authority.includes('@')) return { ok: false, message: 'user info in the URL is not allowed; use secret headers' };
  let parsed: URL;
  try {
    parsed = new URL(`${m[1]!.toLowerCase()}://${authority}`);
  } catch {
    return { ok: false, message: 'the host is not a valid URL host' };
  }
  const rest = m[3]!;
  if (rest.includes('#')) return { ok: false, message: 'a fragment is never sent; remove it' };
  return { ok: true, origin: parsed.origin, protocol: parsed.protocol as 'https:' | 'http:', hostname: parsed.hostname, port: parsed.port, rest };
}

const ParamName = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'letters, digits and _, starting with a letter or _ (up to 64)');
const HeaderName = z.string().regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]{1,128}$/, 'an HTTP header name');
/** `env:NAME` or `file:/abs/path` under `BB_SECRETS_DIR`; the resolver decides what is allowed. */
export const WebhookSecretRef = z.string().trim().min(1).max(1024);

export const WebhookParam = z.strictObject({
  name: ParamName,
  type: z.enum(WEBHOOK_PARAM_TYPES),
  required: z.boolean().default(false),
  description: z.string().max(300).optional(),
});
export type WebhookParam = z.infer<typeof WebhookParam>;

/** A plain header; its value may use Liquid (`{{ user.external_id }}`) but never carries a credential. */
export const WebhookHeader = z.strictObject({
  name: HeaderName,
  value: z
    .string()
    .max(2048)
    .refine((v) => !/[\r\n]/.test(v), 'a header value cannot contain line breaks'),
});
export type WebhookHeader = z.infer<typeof WebhookHeader>;

/** A header whose value comes from a secret reference (`Authorization: Bearer …`); sent as is, never templated. */
export const WebhookSecretHeader = z.strictObject({ name: HeaderName, secretRef: WebhookSecretRef });
export type WebhookSecretHeader = z.infer<typeof WebhookSecretHeader>;

const WebhookUrlTemplate = z
  .string()
  .trim()
  .min(1)
  .max(2048)
  .superRefine((url, ctx) => {
    const split = splitWebhookUrl(url);
    if (!split.ok) ctx.addIssue({ code: 'custom', message: split.message });
  });

const EndpointFields = z.strictObject({
  name: z.string().trim().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  url: WebhookUrlTemplate,
  method: z.enum(WEBHOOK_METHODS).default('POST'),
  /** Content type of a custom body template; the default body (the typed params) is always JSON. */
  contentType: z.enum(WEBHOOK_CONTENT_TYPES).default('application/json'),
  headers: z.array(WebhookHeader).max(20).default([]),
  secretHeaders: z.array(WebhookSecretHeader).max(10).default([]),
  /** HMAC-SHA256 key of `X-BB-Signature`; null — requests are not signed. */
  signingSecretRef: WebhookSecretRef.nullable().default(null),
  params: z.array(WebhookParam).max(50).default([]),
  timeoutMs: z.int().min(WEBHOOK_TIMEOUT_MS.min).max(WEBHOOK_TIMEOUT_MS.max).default(WEBHOOK_TIMEOUT_MS.default),
  maxAttempts: z.int().min(WEBHOOK_MAX_ATTEMPTS.min).max(WEBHOOK_MAX_ATTEMPTS.max).default(WEBHOOK_MAX_ATTEMPTS.default),
  enabled: z.boolean().default(true),
});

/** Header and param rules that need the whole endpoint (also checked on the merged result of a PATCH). */
export function checkWebhookEndpoint(e: { headers?: WebhookHeader[] | undefined; secretHeaders?: WebhookSecretHeader[] | undefined; params?: WebhookParam[] | undefined }, ctx: z.RefinementCtx): void {
  const reserved = new Set<string>(WEBHOOK_RESERVED_HEADERS);
  const seen = new Set<string>();
  const check = (list: Array<{ name: string }> | undefined, key: 'headers' | 'secretHeaders') =>
    (list ?? []).forEach((h, i) => {
      const lower = h.name.toLowerCase();
      if (reserved.has(lower)) ctx.addIssue({ code: 'custom', path: [key, i, 'name'], message: `${h.name} is set by the sender` });
      if (seen.has(lower)) ctx.addIssue({ code: 'custom', path: [key, i, 'name'], message: `${h.name} is listed twice` });
      if (key === 'headers' && looksLikeSecretHeader(h.name)) ctx.addIssue({ code: 'custom', path: [key, i, 'name'], message: `${h.name} looks like a credential; put it in secretHeaders` });
      seen.add(lower);
    });
  check(e.headers, 'headers');
  check(e.secretHeaders, 'secretHeaders');
  const names = (e.params ?? []).map((p) => p.name);
  names.forEach((n, i) => {
    if (names.indexOf(n) !== i) ctx.addIssue({ code: 'custom', path: ['params', i, 'name'], message: `param ${n} is listed twice` });
  });
}

export const CreateWebhookEndpointBody = EndpointFields.superRefine(checkWebhookEndpoint);
export type CreateWebhookEndpointBody = z.infer<typeof CreateWebhookEndpointBody>;

/** PATCH: any subset; the merged endpoint is checked again (`checkWebhookEndpoint`). */
export const UpdateWebhookEndpointBody = z
  .strictObject({
    name: EndpointFields.shape.name.optional(),
    description: z.string().max(500).nullable().optional(),
    url: WebhookUrlTemplate.optional(),
    method: z.enum(WEBHOOK_METHODS).optional(),
    contentType: z.enum(WEBHOOK_CONTENT_TYPES).optional(),
    headers: z.array(WebhookHeader).max(20).optional(),
    secretHeaders: z.array(WebhookSecretHeader).max(10).optional(),
    signingSecretRef: WebhookSecretRef.nullable().optional(),
    params: z.array(WebhookParam).max(50).optional(),
    timeoutMs: z.int().min(WEBHOOK_TIMEOUT_MS.min).max(WEBHOOK_TIMEOUT_MS.max).optional(),
    maxAttempts: z.int().min(WEBHOOK_MAX_ATTEMPTS.min).max(WEBHOOK_MAX_ATTEMPTS.max).optional(),
    enabled: z.boolean().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, 'nothing to change');
export type UpdateWebhookEndpointBody = z.infer<typeof UpdateWebhookEndpointBody>;

/** What an integrator sees (`integrations.manage`): the configuration, secret references but never secret values. */
export interface WebhookEndpointView {
  id: string;
  name: string;
  description: string | null;
  url: string;
  /** The literal origin of `url`; every request goes there. */
  origin: string;
  method: WebhookMethod;
  contentType: WebhookContentType;
  headers: WebhookHeader[];
  secretHeaders: WebhookSecretHeader[];
  signingSecretRef: string | null;
  params: WebhookParam[];
  timeoutMs: number;
  maxAttempts: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  updatedBy: string | null;
}

/** What a composer sees (no `integrations.manage`): enough to pick an endpoint and fill its params. */
export interface WebhookEndpointSummary {
  id: string;
  name: string;
  description: string | null;
  origin: string;
  method: WebhookMethod;
  contentType: WebhookContentType;
  params: WebhookParam[];
  signed: boolean;
  enabled: boolean;
}

/** `*.vcg.example` (any subdomain, not the bare domain) or an exact host. */
export const WebhookDomainPattern = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(\*\.)?([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/, 'a host name, optionally starting with *.');

/** Webhook settings of an environment: the domains its endpoints may call. Empty — no endpoint can be saved. */
export const WebhookSettings = z.strictObject({
  allowedDomains: z
    .array(WebhookDomainPattern)
    .max(100)
    .refine((list) => new Set(list).size === list.length, 'domains must be unique'),
});
export type WebhookSettings = z.infer<typeof WebhookSettings>;

/**
 * Content of the `webhook` channel of a variant or Canvas Message step: the endpoint, a Liquid expression per param
 * (rendered for the recipient, then converted to the param's type), and an optional body template that replaces the
 * default JSON body (the typed params) and can read them as `{{ params.<name> }}`.
 */
export const WebhookChannelContent = z.looseObject({
  endpointId: z.uuid(),
  params: z.record(ParamName, z.string().max(4096)).default({}),
  bodyTemplate: z.string().max(WEBHOOK_BODY_MAX_BYTES).optional(),
});
export type WebhookChannelContent = z.infer<typeof WebhookChannelContent>;

/** The rendered request of one send, as stored with the delivery (secret headers and the signature are added at send time). */
export interface RenderedWebhookRequest {
  method: WebhookMethod;
  url: string;
  contentType: WebhookContentType;
  headers: WebhookHeader[];
  body: string;
}
